import { useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import { useFormikContext } from 'formik';
import useSWR from 'swr';
import { CircularProgress } from '@mui/material';
import { Modal } from '@shared/components/modals/Modal';
import { SERVICES } from '@shared/constants/apiConfig';
import { IConfiguration } from '@modules/admin/AdminTools/ConfigurationTable/types';
import { VehicleFeaturesLoaderWrapper } from '@styles/Shared/StyledLoaders';
import { LoaderBackdrop } from '@shared/components/LoaderBackdrop';
import { IFormAsset } from '../interfaces';
import { processVehicleData } from '../formatBumperResponse';

const LEAD_DATA = 'LEAD_DATA';
const BUMPER_DATATYPE = 'BUMPER_DATATYPE';
export interface IVehicleOptionsModalProps {
  handleClose: () => void;
  displayVehicleOptionsPopup: boolean;
}
export const VehicleOptionsModal = ({ handleClose, displayVehicleOptionsPopup }: IVehicleOptionsModalProps) => {
  const [bumperBuildDataType, setBumperBuildDataType] = useState<number[] | null>(null);
  const { data: configuration, isValidating: isLoadingConfiguration } = useSWR<IConfiguration[]>(
    `${SERVICES.CONFIGURATION}/configurations/all`
  );
  const { values: assetData } = useFormikContext<IFormAsset>();
  const { vin } = assetData;
  const url = `${SERVICES.EXTERNAL}/bumper/build`;
  const hasMutated = useRef(false);
  const {
    data: featureData,
    isValidating,
    mutate,
    error,
  } = useSWR(
    url,
    (url: string) =>
      axios.post(url, {
        vin: vin,
        bumperBuildDataType: bumperBuildDataType,
      }),
    { revalidateOnMount: false }
  );

  useEffect(() => {
    if (configuration && configuration.length > 0 && !isLoadingConfiguration) {
      const bumperBuildDataTypeValue = configuration.find(
        (item) => item.configurationGroup.trim() === LEAD_DATA && item.tokenName.trim() === BUMPER_DATATYPE
      )?.value;
      const bumperBuildDataTypDescription = configuration.find(
        (item) => item.configurationGroup.trim() === LEAD_DATA && item.tokenName.trim() === BUMPER_DATATYPE
      )?.description;

      if (bumperBuildDataTypDescription && bumperBuildDataTypDescription?.length > 0 && bumperBuildDataTypeValue) {
        const bumperAllBuildDescriptionArray = bumperBuildDataTypDescription
          .split(',')
          .map((item) => item.trim())
          .map((item) => item.replace(/\s*=\s*\d+$/, ''))
          .filter((item) => item.length > 0);

        const indexes = bumperBuildDataTypeValue.split(',').map((item) => bumperAllBuildDescriptionArray.indexOf(item) + 1);
        if (indexes && indexes.length > 0) {
          setBumperBuildDataType(indexes);
        }
      }
    }
  }, [configuration, isLoadingConfiguration]);

  useEffect(() => {
    if (bumperBuildDataType && !hasMutated.current) {
      hasMutated.current = true;
      mutate();
    }
  }, [bumperBuildDataType, mutate]);

  const formattedBumper = useMemo(() => {
    if (featureData) {
      return processVehicleData(featureData?.data);
    }
  }, [featureData]);

  return (
    <>
      {(isValidating || isLoadingConfiguration) && <LoaderBackdrop open={!error || isValidating || isLoadingConfiguration} />}
      <Modal
        hideBackdrop
        open={displayVehicleOptionsPopup}
        title={'Vehicle Options'}
        dialogHeight="95%"
        dialogWidth="650px"
        onClose={handleClose}
      >
        {error && !featureData && (!isValidating || !isLoadingConfiguration) ? (
          <VehicleFeaturesLoaderWrapper>
            <p>Unable to retrieve vehicle options.</p>
          </VehicleFeaturesLoaderWrapper>
        ) : !error && (isValidating || isLoadingConfiguration) ? (
          <VehicleFeaturesLoaderWrapper>
            <CircularProgress color="secondary" />
          </VehicleFeaturesLoaderWrapper>
        ) : (
          <pre className="">{formattedBumper}</pre>
        )}
      </Modal>
    </>
  );
};